import { defineStore } from "pinia"
import axios from "axios"
import { useToast } from "@/components/ui/toast"
import i18n from '@/locales/i18n'

type EntityType = string

const { toast } = useToast()

function entityParamRoot(entity: string | null | undefined) {
  const name = String(entity || "").replace(/^\//, "").replace(/\.json$/, "").split("/")[0]
  const keepAsIs = new Set([
    "kunde",
    "rechtsgrundlagen",
    "pflichten",
    "rechtsgrundlagenmatrix",
    "pflichtenmatrix",
  ])
  if (keepAsIs.has(name)) return name
  return name.endsWith("s") ? name.slice(0, -1) : name
}

export const useEntityStore = defineStore("entities", {
  state: () => ({
    entities: {} as Record<EntityType, any[]>,
    currentItem: {} as Record<EntityType, any | null>,
    showEditor: {} as Record<EntityType, boolean>,
    /** True while saveEditor PATCH/POST is in flight (per entity endpoint key, e.g. "kunde"). */
    editorSaving: {} as Record<string, boolean>,
  }),
  actions: {
    setItems(entity: EntityType, items: any[]) {
      this.entities[entity] = items
    },
    openEditor(entity: EntityType, item: any) {
	  console.log("openEditor=" + entity + " " + JSON.stringify(item))
      this.currentItem[entity] = { ...item } // clone to avoid mutating original
      this.showEditor[entity] = true
    },
    closeEditor(entity: EntityType) {
      this.currentItem[entity] = null
      this.showEditor[entity] = false
    },
	async saveEditor(
	  entity: EntityType,
	  saveEntity: EntityType,
	  postAction: (item?: any, responseData?: any) => void,
	  filter?: (key: string, value: any) => boolean,
	  optimisticAction?: (item: any) => void,
	  rollbackAction?: (item: any) => void
	) {
	  const item = this.currentItem[entity];
	  console.log("saveEditor item=" + JSON.stringify(item))
	  if (!item) return;

	  const entityKey = String(entity)
	  const optimisticItem = { ...item }
	  this.editorSaving[entityKey] = true
	  try {
	    const token = document
	      .querySelector('meta[name="csrf-token"]')
	      ?.getAttribute("content");
	    if (token) axios.defaults.headers.common["X-CSRF-Token"] = token;
	    
	    // Add locale header for saving translations
	    const currentLocale = i18n.global.locale.value || 'de'
	    axios.defaults.headers.common['X-Locale'] = currentLocale

		let formEntity = entityParamRoot(saveEntity);
	    const formData = new FormData();
	    for (const [key, value] of Object.entries(item)) {
		  console.log("useEntityStore key=" + key + " value=" + value)
		  if (filter && !filter(key, value)) {
			continue;
		  }
	      if (value instanceof File) {
	        // Handle file fields
	        formData.append(`${formEntity}[${key}]`, value, value.name);
	        formData.append(key, value, value.name);
	      } else if (value !== null && value !== undefined) {
	        formData.append(`${formEntity}[${key}]`, String(value));
	        formData.append(key, String(value));
	      }
	    }
		optimisticAction?.(optimisticItem)

		let responseData: any = null
		if (item.id) {
			const response = await axios.patch(`${saveEntity}/${item.id}`, formData, {
			  headers: { "Content-Type": "multipart/form-data" },
			});
			responseData = response.data
		} else {
			const response = await axios.post(`${saveEntity}`, formData, {
			  headers: { "Content-Type": "multipart/form-data" },
			});
			responseData = response.data
		}

	    toast({
	      title: "Saved",
	      description: "Speichern erfolgreich.",
	    });

	    postAction(optimisticItem, responseData);
	    this.closeEditor(entity);
	  } catch (error: any) {
		rollbackAction?.(optimisticItem)
		console.log("error=" + error)
	    const message =
	      error.response?.data?.message ||
	      error.response?.statusText ||
		  error.message ||
	      "Failed to save row.";

	    toast({
	      title: "Error",
	      description: message,
	      variant: "destructive",
	    });
	  } finally {
	    this.editorSaving[entityKey] = false
	  }
	},
    updateField(entity: EntityType, key: string, value: any) {
      if (this.currentItem[entity]) {
        this.currentItem[entity][key] = value
      }
    },
  },
})
